<template>
  <el-dialog :visible.sync="dialogVisible" width="700px" :show-close="false">
    <div class="product-header">
      <img :src="projectInfo.icon" class="product-icon" />
      <div class="product-info">
        <span class="product-name">{{ projectInfo.projectName }}</span>
        <span
          :class="[
            'product-tag',
            projectInfo.saleType === 1 ? 'pre-sale' : 'open-sale',
          ]"
        >
          {{
            projectInfo.saleType === 1
              ? $t("mall.pre_sale")
              : $t("mall.open_sale")
          }}
        </span>
      </div>
    </div>

    <div class="quantity-section">
      <span class="section-label">{{
        $t("confirmBuy.subscription_shares")
      }}</span>
      <div class="quantity-box">
        <div class="quantity-control">
          <div class="quantity-btn" @click="decreaseQuantity">-</div>
          <input
            class="quantity-input"
            type="number"
            v-model="quantity"
            @input="handleQuantityBlur"
          />
          <div class="quantity-btn" @click="increaseQuantity">+</div>
        </div>
        <span class="quantity-tip"
          >{{ $t("confirmBuy.available") }} {{ projectInfo.buySharesMax }}
          {{ $t("confirmBuy.shares") }} | 1{{ $t("confirmBuy.per_share") }}={{
            projectInfo.perShareAssetAmount
          }}
          {{ projectInfo.saleAssetType }}</span
        >
      </div>
    </div>

    <div class="info-row">
      <span class="info-label">{{ $t("confirmBuy.subscription_value") }}</span>
      <div class="info-value-wrap">
        <span class="info-value"
          >{{ formatNumber(subscriptionValue) }} {{ projectInfo.coin }}</span
        >
        <span class="info-tip"
          >{{ $t("mallDetail.issue_price") }}
          {{ projectInfo.issueUsdPrice }} USD</span
        >
      </div>
    </div>


    <div class="info-row">
      <span class="info-label">{{ $t("mallDetail.purchase_price") }}</span>
      <div class="info-value-wrap">
        <span class="info-value">{{ projectInfo.priceUsd }} USD</span>
      </div>
    </div>



    <div class="info-row highlight">
      <span class="info-label">{{ $t("confirmBuy.payment_total") }}</span>
      <span class="info-value"
        >{{ formatNumber(totalUsd) }} USD /
        {{ formatNumber(totalHkd) }} HKD</span
      >
    </div>

    <div class="divider"></div>

    <div class="pay-method-section">
      <div class="c-flex-left mb20">
        <span class="section-title">{{ $t("confirmBuy.payment") }}</span>
        <span class="section-subtitle ml12">{{
          $t("confirmBuy.pay_from")
        }}</span>
      </div>

      <div
        v-for="(method, index) in payMethods"
        :key="index"
        :class="[
          'pay-method-item',
          { active: selectedPayMethod === method.value },
        ]"
        @click="selectedPayMethod = method.value"
      >
        <div class="method-radio">
          <div
            v-if="selectedPayMethod === method.value"
            class="radio-inner"
          ></div>
        </div>
        <div class="method-info">
          <span class="method-name">{{ method.label }}</span>
          <span class="method-balance"
            >{{ $t("confirmBuy.balance") }}: {{ method.balance }}
            {{ method.value }}</span
          >
        </div>
        <!-- <span v-if="method.rate" class="exchange-rate">{{ method.rate }}</span> -->
        <span class="recharge-btn" @click.stop="handleRecharge(method.value)">{{
          $t("confirmBuy.recharge")
        }}</span>
      </div>
    </div>

    <div class="dialog-footer">
      <button class="cancel-btn" @click="dialogVisible = false">
        {{ $t("confirmBuy.cancel") }}
      </button>
      <button :class="['confirm-btn', { disabled: !hasBalance }]" @click="handleConfirm">
        {{ $t("confirmBuy.confirm_buy") }}
      </button>
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";
export default {
  name: "ConfirmBuyDialog",
  props: {
    projectInfo: {
      type: Object,
      default: () => ({
        id: 0,
        projectName: "",
        saleType: 1,
        icon: "",
        priceUsd: 0,
        perShareAssetAmount: 0,
        saleAssetType: "",
        minBuyShares: 1,
        maxBuyShares: 1,
        buySharesMax: 1,
      }),
    },
  },
  data() {
    return {
      dialogVisible: false,
      quantity: 1,
      selectedPayMethod: "USD",
      payMethods: [
        { label: "USD", value: "USD", balance: 0, rate: "" },
        { label: "HKD", value: "HKD", balance: 0, rate: "" },
      ],
    };
  },
  computed: {
    ...mapState({
      user: "user",
      errorCode: "errorCode",
    }),
    subscriptionValue() {
      return this.quantity * this.projectInfo.perShareAssetAmount;
    },
    totalUsd() {
      return this.quantity * this.projectInfo.priceUsd;
    },
    totalHkd() {
      return this.totalUsd * 7.8;
    },
    isQuantityExceeded() {
      const maxQuantity = Math.min(
        this.projectInfo.maxBuyShares || 0,
        this.projectInfo.buySharesMax || 0,
      );
      return this.quantity > maxQuantity;
    },
    isQuantityBelowMin() {
      const minQuantity = this.projectInfo.minBuyShares || 1;
      return this.quantity < minQuantity;
    },
    hasBalance() {
      return this.payMethods.some(method => Number(method.balance) > 0);
    },
  },
  methods: {
    open() {
      this.dialogVisible = true;
      this.quantity = this.projectInfo.minBuyShares || 1;
      this.selectedPayMethod = "USD";
      this.loadUserAssets();
    },
    formatNumber(num) {
      if (!num && num !== 0) return "0";
      return Number(num).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      });
    },
    decreaseQuantity() {
      if (this.quantity > this.projectInfo.minBuyShares) {
        this.quantity--;
      }
    },
    increaseQuantity() {
      const maxQuantity = Math.min(
        this.projectInfo.maxBuyShares,
        this.projectInfo.buySharesMax,
      );
      if (this.quantity < maxQuantity) {
        this.quantity++;
      } else {
        this.$message.warning(this.$t("confirmBuy.exceeded_tip"));
      }
    },
    handleQuantityBlur() {
      const minQuantity = this.projectInfo.minBuyShares || 1;
      const maxQuantity = Math.min(
        this.projectInfo.maxBuyShares,
        this.projectInfo.buySharesMax,
      );
      let val = parseInt(this.quantity) || 1;

      if (val < minQuantity) {
        val = minQuantity;
      } else if (val > maxQuantity) {
        val = maxQuantity;
        this.$message.warning(this.$t("confirmBuy.exceeded_tip"));
      }

      this.quantity = val;
    },
    loadUserAssets() {
      this.$axios
        .post("/userAssets/queryAssetList", {
          accountType: "spot",
          legalCurrency: "HKD",
        })
        .then((res) => {
          if (
            res.data.code === this.errorCode.ok &&
            res.data.data &&
            res.data.data.userAssetRsps
          ) {
            const usdtAsset = res.data.data.userAssetRsps.find(
              (item) => item.assetType === "USDT",
            );
            if (usdtAsset) {
              const usdMethod = this.payMethods.find(
                (item) => item.value === "USD",
              );
              if (usdMethod) {
                usdMethod.balance = usdtAsset.balanceAvailable || 0;
              }
            }

            const hkdAsset = res.data.data.userAssetRsps.find(
              (item) => item.assetType === "HKD",
            );
            if (hkdAsset) {
              const hkdMethod = this.payMethods.find(
                (item) => item.value === "HKD",
              );
              if (hkdMethod) {
                hkdMethod.balance = hkdAsset.balanceAvailable || 0;
                hkdMethod.rate = "1.25 HKD = 1USD";
              }
            }
          }
        });
    },
    handleRecharge(methodValue) {
      this.dialogVisible = false;
      this.$emit("recharge", methodValue);
    },
    handleConfirm() {
      if (this.isQuantityBelowMin) {
        const minQuantity = this.projectInfo.minBuyShares || 1;
        this.$message.warning(
          this.$t("mallDetail.min_buy_shares") + ": " + minQuantity,
        );
        return;
      }
      if (this.isQuantityExceeded) {
        this.$message.warning(this.$t("confirmBuy.exceeded_tip"));
        return;
      }
      if (!this.hasBalance) {
        this.$message.warning(this.$t("confirmBuy.insufficient_balance"));
        return;
      }
      this.dialogVisible = false;
      this.$emit("confirm", {
        projectId: this.projectInfo.id,
        shares: this.quantity,
        payAssetType: this.selectedPayMethod,
      });
    },
  },
};
</script>

<style lang="less" scoped>
/deep/.el-dialog {
  background: #ffffff;
  border-radius: 12px;
  .el-dialog__body {
    padding-top: 0;
    position: relative;
    padding: 30px;
  }
}

.close-img {
  top: 20px;
  right: 20px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  position: absolute;
  z-index: 10;
}

.product-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.product-icon {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background-color: #333;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-name {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
}

.product-tag {
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 4px;
  width: fit-content;

  &.pre-sale {
    color: #07c160;
    background-color: rgba(7, 193, 96, 0.1);
    border: 1px solid #07c160;
  }

  &.open-sale {
    color: #4063ff;
    background-color: rgba(64, 99, 255, 0.1);
    border: 1px solid #4063ff;
  }
}

.quantity-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.section-label {
  font-size: 14px;
  color: #999999;
}

.section-subtitle {
  font-size: 12px;
  color: #999999;
  display: block;
}
.quantity-box {
  display: flex;
  flex-flow: column;
  flex: 1;
}

.quantity-control {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
}

.quantity-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  border-radius: 50%;
  font-size: 20px;
  color: #333333;
  cursor: pointer;
}

.quantity-input {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  width: 50px;
  text-align: center;
  border: none;
  outline: none;
  background: transparent;
}

.quantity-tip {
  font-size: 12px;
  color: #999999;
  margin-left: auto;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;

  &.highlight {
    .info-value {
      color: #ff4d4f;
      font-weight: 600;
      font-size: 18px;
    }
  }
}

.info-label {
  font-size: 14px;
  color: #999999;
}

.info-value-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.info-value {
  font-size: 14px;
  color: #333333;
}

.info-tip {
  font-size: 12px;
  color: #999999;
}

.divider {
  height: 1px;
  background: #f0f0f0;
  margin: 20px 0;
}

.pay-method-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333333;
  display: block;
}

.pay-method-item {
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 12px;
  border: 2px solid transparent;
  cursor: pointer;

  &.active {
    border-color: #4063ff;
    background-color: #f5f7ff;
  }

  .method-radio {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid #cccccc;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;

    .radio-inner {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #4063ff;
    }
  }

  .method-info {
    flex: 1;

    .method-name {
      font-size: 14px;
      color: #333333;
      display: block;
    }

    .method-balance {
      font-size: 12px;
      color: #999999;
      margin-top: 4px;
      display: block;
    }
  }

  .exchange-rate {
    font-size: 12px;
    color: #999999;
    margin-right: 12px;
  }

  .recharge-btn {
    font-size: 14px;
    color: #4063ff;
  }
}

.dialog-footer {
  display: flex;
  gap: 16px;
}

.cancel-btn {
  flex: 1;
  height: 44px;
  border: 1px solid #e2e3e3;
  border-radius: 8px;
  font-size: 14px;
  color: #666666;
  background-color: #ffffff;
  cursor: pointer;
}

.confirm-btn {
  flex: 1;
  height: 44px;
  background: linear-gradient(135deg, #4063ff 0%, #6b8cff 100%);
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
</style>
